import { BlogPostType } from '../../types';
import { serviceBlogPosts } from './serviceBlogs';

// All blog posts (services + additional content)
export const blogPosts: BlogPostType[] = [
  ...serviceBlogPosts, // Comprehensive service blogs
  
  // Additional SEO & Strategy Posts
  {
    id: 'google-ads-playbook',
    title: 'Google Ads Playbook: From Clicks to Customers',
    excerpt:
      'A practical framework to structure campaigns, write winning ads, and track revenue — not just clicks.',
    content:
      'We outline account structure, keyword intent groups, ad messaging, landing page alignment, and measurement practices to make Google Ads drive qualified leads and sales.',
    author: 'Performance Team',
    publishedAt: new Date().toISOString(),
    category: 'Paid Ads',
    tags: ['Google Ads', 'CRO', 'Measurement'],
    image:
      'https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?auto=format&fit=crop&w=1600&q=80',
    slug: 'google-ads-playbook',
  },
  {
    id: 'meta-ads-blueprint',
    title: 'Meta Ads Blueprint: Scaling Facebook & Instagram Campaigns',
    excerpt:
      'Creative testing, audience building, and funnel design to grow leads and sales on Meta reliably.',
    content:
      'From hooks and creative angles to lead forms and remarketing flows, this blueprint shows how to build Meta campaigns that scale with data-driven creative testing.',
    author: 'Social Team',
    publishedAt: new Date().toISOString(),
    category: 'Paid Ads',
    tags: ['Meta Ads', 'Creatives', 'Remarketing'],
    image:
      'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1600&q=80',
    slug: 'meta-ads-blueprint',
  },
  {
    id: 'youtube-ads-guide',
    title: 'YouTube Ads Guide: Win Attention, Earn Trust',
    excerpt:
      'Story structures, targeting models, and measurement to turn views into pipeline on YouTube.',
    content:
      'We cover ad formats, targeting stacks, creative frameworks, and how to align messaging with buyer awareness to grow subscribers and sales.',
    author: 'Video Team',
    publishedAt: new Date().toISOString(),
    category: 'Video',
    tags: ['YouTube Ads', 'Creative', 'Targeting'],
    image:
      'https://images.unsplash.com/photo-1478720568477-152d9b164e26?auto=format&fit=crop&w=1600&q=80',
    slug: 'youtube-ads-guide',
  },
  {
    id: 'custom-web-dev-checklist',
    title: 'Custom Web Development Checklist for Growth-Focused Brands',
    excerpt:
      'A practical checklist covering UX, performance, integrations, and security for modern, scalable sites.',
    content:
      'This checklist helps teams plan features, choose the right stack, and ship fast websites that convert — with room to scale.',
    author: 'Dev Team',
    publishedAt: new Date().toISOString(),
    category: 'Development',
    tags: ['Web Dev', 'Performance', 'Security'],
    image:
      'https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1600&q=80',
    slug: 'custom-web-dev-checklist',
  },
  {
    id: 'content-marketing-system',
    title: 'Content Marketing System: From Ideas to Revenue',
    excerpt:
      'An end-to-end system for research, production, distribution, and measurement across channels.',
    content:
      'We show how to build topic clusters, publish consistently, repurpose across formats, and measure business impact.',
    author: 'Content Team',
    publishedAt: new Date().toISOString(),
    category: 'Content',
    tags: ['Blogs', 'Distribution', 'Analytics'],
    image:
      'https://images.unsplash.com/photo-1526481280695-3c469c2f58c0?auto=format&fit=crop&w=1600&q=80',
    slug: 'content-marketing-system',
  },
  {
    id: 'traditional-seo-guide',
    title: 'Traditional SEO Guide: Technical, Content, and Links',
    excerpt:
      'A balanced approach to technical fixes, high‑intent content, and ethical links to grow rankings steadily.',
    content:
      'From crawlability and Core Web Vitals to SERP‑friendly content and white-hat outreach, this guide outlines how to build compounding search growth.',
    author: 'SEO Team',
    publishedAt: new Date().toISOString(),
    category: 'SEO',
    tags: ['Technical SEO', 'Content', 'Links'],
    image:
      'https://images.unsplash.com/photo-1454165205744-3b78555e5572?auto=format&fit=crop&w=1600&q=80',
    slug: 'traditional-seo-guide',
  },
  {
    id: 'ppc-foundations',
    title: 'PPC Foundations: Structuring Accounts That Scale',
    excerpt:
      'Exact match cores, intent‑based ad groups, and negative maps for efficient spend across networks.',
    content:
      'We share patterns for search vs. performance max, brand protection, remarketing layers, and the handoff to CRO.',
    author: 'Performance Team',
    publishedAt: new Date().toISOString(),
    category: 'Paid Ads',
    tags: ['PPC', 'Structure', 'Efficiency'],
    image:
      'https://images.unsplash.com/photo-1434626881859-194d67b2b86f?auto=format&fit=crop&w=1600&q=80',
    slug: 'ppc-foundations',
  },
  {
    id: 'bing-ads-advantage',
    title: 'Bing Ads Advantage: Lower CPC, Higher Intent',
    excerpt:
      'How to leverage Microsoft Ads, audience network, and LinkedIn data for B2B wins.',
    content:
      'Set up import, tune bids by audience, and align landing pages to capture incremental volume beyond Google.',
    author: 'Performance Team',
    publishedAt: new Date().toISOString(),
    category: 'Paid Ads',
    tags: ['Bing Ads', 'B2B', 'Targeting'],
    image:
      'https://images.unsplash.com/photo-1483478550801-ceba5fe50e8e?auto=format&fit=crop&w=1600&q=80',
    slug: 'bing-ads-advantage',
  },
  {
    id: 'web-design-for-performance',
    title: 'Web Design for Performance: UX that Converts',
    excerpt:
      'Above‑the‑fold clarity, frictionless forms, and social proof that earn trust quickly.',
    content:
      'We cover layout systems, copy blocks, and micro‑interactions that increase leads without sacrificing brand.',
    author: 'Design Team',
    publishedAt: new Date().toISOString(),
    category: 'Development',
    tags: ['Web Design', 'CRO', 'UX'],
    image:
      'https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1600&q=80',
    slug: 'web-design-for-performance',
  },
  {
    id: 'wordpress-vs-shopify',
    title: 'WordPress vs Shopify: Choose the Right Platform',
    excerpt:
      'A practical comparison for content‑heavy sites vs. ecommerce catalogs, including maintenance and SEO.',
    content:
      'We compare cost, customization, plugins/apps, checkout, and long‑term scalability to guide your decision.',
    author: 'Dev Team',
    publishedAt: new Date().toISOString(),
    category: 'Development',
    tags: ['WordPress', 'Shopify', 'Ecommerce'],
    image:
      'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1600&q=80',
    slug: 'wordpress-vs-shopify',
  },
  {
    id: 'app-development-roadmap',
    title: 'App Development Roadmap for Startups',
    excerpt:
      'From MVP scope to analytics and release cycles: a simple roadmap that avoids rework.',
    content:
      'Define must‑have features, choose stack, instrument analytics, and build a release cadence aligned with feedback.',
    author: 'Dev Team',
    publishedAt: new Date().toISOString(),
    category: 'Development',
    tags: ['Apps', 'Roadmap', 'MVP'],
    image:
      'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1600&q=80',
    slug: 'app-development-roadmap',
  },
  {
    id: 'whatsapp-api-marketing',
    title: 'WhatsApp API Marketing: Personal at Scale',
    excerpt:
      'Templates, opt‑ins, and flows that keep communication compliant and high‑response.',
    content:
      'We outline use cases, consent, message templates, and routing to build a reliable WhatsApp funnel.',
    author: 'Automation Team',
    publishedAt: new Date().toISOString(),
    category: 'Social',
    tags: ['WhatsApp', 'Automation', 'Templates'],
    image:
      'https://images.unsplash.com/photo-1512428559087-560fa5ceab42?auto=format&fit=crop&w=1600&q=80',
    slug: 'whatsapp-api-marketing',
  },
  {
    id: 'social-media-system',
    title: 'Social Media System: Consistency that Compounds',
    excerpt:
      'Plan pillars, batch content, and measure what matters across Facebook, Instagram, and LinkedIn.',
    content:
      'Set up a monthly calendar, reuse creatives smartly, and align posts with offers and seasonality.',
    author: 'Social Team',
    publishedAt: new Date().toISOString(),
    category: 'Social',
    tags: ['SMM', 'Calendars', 'Repurposing'],
    image:
      'https://images.unsplash.com/photo-1492724441997-5dc865305da7?auto=format&fit=crop&w=1600&q=80',
    slug: 'social-media-system',
  },
  {
    id: 'platform-specific-creatives',
    title: 'Platform‑Specific Creatives: Facebook, Instagram, YouTube',
    excerpt:
      'Hook styles, aspect ratios, and CTAs that fit each platform’s feed and behavior.',
    content:
      'Design patterns for thumb‑stop, retention, and action on each platform to improve ROAS.',
    author: 'Creative Team',
    publishedAt: new Date().toISOString(),
    category: 'Creative',
    tags: ['Creatives', 'ROAS', 'Video'],
    image:
      'https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1600&q=80',
    slug: 'platform-specific-creatives',
  },
  {
    id: 'facebook-ads-leadgen',
    title: 'Facebook Lead Gen: Forms, Funnels, and Follow-Up',
    excerpt:
      'How to design Meta lead forms, qualification flows, and remarketing to turn form fills into revenue.',
    content:
      'We cover intent filters, routing to CRM, nurturing cadences, and creative patterns that keep CPL low while improving qualification.',
    author: 'Social Team',
    publishedAt: new Date().toISOString(),
    category: 'Paid Ads',
    tags: ['Facebook Ads', 'Leads', 'Remarketing'],
    image:
      'https://images.unsplash.com/photo-1483478550801-ceba5fe50e8e?auto=format&fit=crop&w=1600&q=80',
    slug: 'facebook-ads-leadgen',
  },
  {
    id: 'instagram-growth-2025',
    title: 'Instagram Growth 2025: Content, Collabs, Conversions',
    excerpt:
      'Reels hooks, carousel playbooks, and creator collabs to grow followers and sales.',
    content:
      'Learn weekly content cadences, CTA placements, and how to tie IG engagement to website conversions and remarketing.',
    author: 'Social Team',
    publishedAt: new Date().toISOString(),
    category: 'Social',
    tags: ['Instagram', 'Reels', 'Collabs'],
    image:
      'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=crop&w=1600&q=80',
    slug: 'instagram-growth-2025',
  },
  {
    id: 'linkedin-b2b-pipeline',
    title: 'LinkedIn for B2B Pipeline: Targeting, Content, Outreach',
    excerpt:
      'ICP targeting, thought-leadership posting, and message sequencing that earns meetings.',
    content:
      'We outline company + personal posting, retargeting, and polite outreach frameworks to fill B2B calendars.',
    author: 'B2B Team',
    publishedAt: new Date().toISOString(),
    category: 'Social',
    tags: ['LinkedIn', 'B2B', 'Outbound'],
    image:
      'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1600&q=80',
    slug: 'linkedin-b2b-pipeline',
  },
  {
    id: 'pinterest-ecommerce',
    title: 'Pinterest for Ecommerce: Pins that Drive Carts',
    excerpt:
      'Creative specs, keywording, and boards that send high-intent traffic to product pages.',
    content:
      'We cover seasonality, rich pins, and how to align Pinterest audiences with remarketing for better ROAS.',
    author: 'Social Team',
    publishedAt: new Date().toISOString(),
    category: 'Social',
    tags: ['Pinterest', 'Ecommerce', 'Creatives'],
    image:
      'https://images.unsplash.com/photo-1505740106531-4243f3831c78?auto=format&fit=crop&w=1600&q=80',
    slug: 'pinterest-ecommerce',
  },
  {
    id: 'tiktok-ads-playbook',
    title: 'TikTok Ads Playbook: Native Creative that Converts',
    excerpt:
      'UGC-style hooks, retention beats, and offers that feel native to TikTok.',
    content:
      'Script formulas, spark ads, and comment stacking to improve CTR and CPA on TikTok.',
    author: 'Creative Team',
    publishedAt: new Date().toISOString(),
    category: 'Creative',
    tags: ['TikTok', 'UGC', 'Ads'],
    image:
      'https://images.unsplash.com/photo-1481277542470-605612bd2d61?auto=format&fit=crop&w=1600&q=80',
    slug: 'tiktok-ads-playbook',
  },
  {
    id: 'threads-brand-voice',
    title: 'Threads: Building Brand Voice Without the Noise',
    excerpt:
      'Conversational posting, quick prompts, and community replies that grow reach on Threads.',
    content:
      'Lightweight content loops, response templates, and how to repurpose without feeling automated.',
    author: 'Social Team',
    publishedAt: new Date().toISOString(),
    category: 'Social',
    tags: ['Threads', 'Community', 'Voice'],
    image:
      'https://images.unsplash.com/photo-1522199992905-29661a13f16b?auto=format&fit=crop&w=1600&q=80',
    slug: 'threads-brand-voice',
  },
  {
    id: 'whatsapp-marketing-flows',
    title: 'WhatsApp Marketing Flows: Opt-ins to Re-engagement',
    excerpt:
      'Consent-first templates, segmentation, and drip flows that stay compliant and convert.',
    content:
      'Opt-in methods, template crafting, and follow-up sequences to keep response rates high without spamming.',
    author: 'Automation Team',
    publishedAt: new Date().toISOString(),
    category: 'Social',
    tags: ['WhatsApp', 'Flows', 'Compliance'],
    image:
      'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=1600&q=80',
    slug: 'whatsapp-marketing-flows',
  },
  {
    id: 'consulting-roi',
    title: 'Marketing Consulting: How to Prove ROI Fast',
    excerpt:
      'Engagement models, lighthouse wins, and dashboards that keep leadership bought in.',
    content:
      'Set goals, align experiments, and report with clarity so consulting engagements show impact early.',
    author: 'Consulting Team',
    publishedAt: new Date().toISOString(),
    category: 'Consulting',
    tags: ['Consulting', 'ROI', 'Reporting'],
    image:
      'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1600&q=80',
    slug: 'consulting-roi',
  },
  {
    id: 'white-label-delivery',
    title: 'White Label Delivery: Playbooks Your Partners Can Trust',
    excerpt:
      'Onboarding, SLAs, and communication rhythms that keep white-label clients happy.',
    content:
      'We outline delivery checklists, QA, and reporting templates to make white-label relationships durable.',
    author: 'Partnerships Team',
    publishedAt: new Date().toISOString(),
    category: 'Consulting',
    tags: ['White Label', 'Process', 'Client Success'],
    image:
      'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1600&q=80',
    slug: 'white-label-delivery',
  },
];

export const categories = ['All', 'SEO', 'Paid Ads', 'Social', 'Video', 'Development', 'Content', 'Consulting', 'Email Marketing', 'AI Services'];